import moment from 'moment';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {CellLock, RangeLock} from 'notebook/tables/excess/AST';
import {RelativeCellAddress} from 'notebook/tables/excess/Evaluator';
import {Token} from 'notebook/tables/excess/Tokenizer';
import {
  BaseCellError,
  BaseCellErrors,
  BaseTypedValueType,
  PotentialCellErrors,
  Table,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {
  BooleanTypedValue,
  NumberTypedValue,
  ScientificTypedValue,
  StringTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {generateUniqueName} from 'notebook/util/GenerateUniqueName';
import {USER_EDIT_DATE_FORMAT, USER_EDIT_DATETIME_FORMAT} from 'util/DateConstants';
import ErrorSubclass from 'util/ErrorSubclass';

import {EvaluatedCell, OutputTableMap} from './Evaluator';
// Legacy values that control the fixed precision (digits after the decimal point) that can be applied to
// a table cell, used for rounding, etc. These constraints have issues, as they can conflate fixed precision
// decimal fractions and significant digits of precision in some cases.
export const MIN_PRECISION = 0;
export const MAX_PRECISION = 20;

// Threshold at which a floating point formula output should be automatically displayed in scientific
// notation rather than as a decimal. This value matches Python's native float formatting, which is used by
// Schemas when storing floating point schema field values.
export const DISPLAY_AS_SCIENTIFIC_THRESHOLD = 1e-4;

const NUM_LETTERS = 26;
const START_LETTER = 65;

const SECONDS_PER_DAY = 86400;
// Date that will have serial number 0
const EPOCH_ZERO_DATE = moment('1899-12-30');

export const NUMBER_REGEX = /[+-]?\s*(?:(\d+)(\.\d*)?|(\.\d+))([eE][+-]?\d+)?/.source;
export const CASE_SENSITIVE_BOOLEAN_REGEX = /(?:(TRUE)|(FALSE))/.source;
export const BOOLEAN_REGEX = new RegExp(CASE_SENSITIVE_BOOLEAN_REGEX, 'i');

// ONLY_<TYPE>_REGEX are used to parse cell.text when it is not part of a formula (i.e. prefixed with `=`)
// Excel, Sheets, and Quip confusingly only strip whitespace for numbers, not booleans.
// ONLY_STRING_REGEX is not explicitly defined because we will default to it when parsing
// non-formula text. This will change when we add support for the single quote prefix (e.g. '123)
const ONLY_NUMBER_REGEX = new RegExp(`^\\s*${NUMBER_REGEX}\\s*$`);
const ONLY_BOOLEAN_REGEX = new RegExp(`^${CASE_SENSITIVE_BOOLEAN_REGEX}$`, 'i');
/** Matches when the NUMBER checked by NUMBER_REGEX has any leading zeroes. Does not check if NUMBER first. */
export const NUMBER_HAS_LEADING_ZEROES_REGEX = /^[+-]?\s*0+\d/;

const ZERO_ARGUMENT_FUNCTIONS = ['TRUE', 'FALSE', 'NULL', 'PI'];
export const ZERO_ARGUMENT_FUNCTIONS_REGEX = new RegExp(`^(${ZERO_ARGUMENT_FUNCTIONS.join('|')})$`, 'i');

export const ESCAPED_QUOTE_REGEX = '""';
export const STRING_REGEX = new RegExp(`"((?:[^"]|${ESCAPED_QUOTE_REGEX})*)"`).source;
export enum ValueType {
  SCIENTIFIC = 'SCIENTIFIC',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  ARRAY = 'ARRAY',
  DATETIME = 'DATETIME',
  BOOLEAN = 'BOOLEAN',
}
export const VALUE_TYPES = {
  SCIENTIFIC: ValueType.SCIENTIFIC as ValueType.SCIENTIFIC,
  NUMBER: ValueType.NUMBER as ValueType.NUMBER,
  STRING: ValueType.STRING as ValueType.STRING,
  ARRAY: ValueType.ARRAY as ValueType.ARRAY,
  DATETIME: ValueType.DATETIME as ValueType.DATETIME,
  BOOLEAN: ValueType.BOOLEAN as ValueType.BOOLEAN,
};

export const NUMERIC_TYPES = [VALUE_TYPES.SCIENTIFIC, VALUE_TYPES.NUMBER];

const TABLE_NAME_DEFAULT_PREFIX = 'Table';

// Singleton used to indicate a selector that has been deleted. This needs to be an instance of a class as
// opposed to an object because otherwise cloneWithReuse will deep-copy it causing reference equality to
// fail.
class MissingSelector {}
export const MISSING_SELECTOR = new MissingSelector();

// Upper decimal digit precision bound for floating point values.
// Keep in sync with benchling/notebook/structured_tables/export/typed_value.py:MAX_FLOAT_DECIMAL_DIGITS
export const MAX_FLOAT_DECIMAL_DIGITS = 15;

/**
 * Apply Benchling's bound for the number of decimal digits that can be supported
 * for floating point values to a binary float variable.
 *
 * NOTE: Edits should be reflected in benchling/notebook/structured_tables/export/typed_value.py:cap_max_float_decimal_digits.
 */
export function capMaxFloatDecimalDigits(raw: number): number {
  return parseFloat(raw.toPrecision(MAX_FLOAT_DECIMAL_DIGITS));
}

/**
 * @param {object} typedValue - for an excess value, {rawValue, type, defaultPrecision}. For a non-excess
 *   typed value, this should be an object with a "type" key and whatever other keys necessary to represent
 *   that type.
 */
export function astValue(typedValue: TypedValue): TypedValue {
  // For numeric types, tests currently expect defaultPrecision to be defined.
  switch (typedValue.type) {
    case BaseTypedValueType.NUMBER:
    case BaseTypedValueType.SCIENTIFIC: {
      const defaultPrecision = typedValue.defaultPrecision == null ? null : typedValue.defaultPrecision;
      return {...typedValue, defaultPrecision};
    }
    default:
      return typedValue;
  }
}

/**
 * Helper function to strip leading zeros from a string containing an integer value. This is safer than
 * parsing the integer value because user input may produce inputs that exceed Number.MAX_SAFE_INTEGER.
 *
 * @param digits a string containing only the digits 0-9
 * @returns the input string with any leading zeros removed
 */
function stripLeadingZeros(digits: string): string {
  return digits.replace(/^0+/, '');
}

/**
 * Parse a value token into an AST value object. If the caller has already matched against NUMBER_REGEX or
 * STRING_REGEX, the match can be passed in to avoid a second, redundant call to RegExp.exec()
 *
 * NOTE: Edits should be reflected in benchling/notebook/structured_tables/export/typed_value.py:parse_numeric_typed_value_from_string.
 *
 * @param {String} text - the text to parse
 * @param {Object?} numberMatch - result from matching against NUMBER_REGEX
 * @param {Object?} stringMatch - result from matching against STRING_REGEX
 * @param {Object?} booleanMatch - result from matching against BOOLEAN_REGEX
 * @return {Object} - standardized AST evaluation output
 */
export function parseValue(
  text: string,
  numberMatch: RegExpExecArray | null = null,
  stringMatch: RegExpExecArray | null = null,
  booleanMatch: RegExpExecArray | null = null
): StringTypedValue | ScientificTypedValue | NumberTypedValue | BooleanTypedValue {
  // If the caller did not give a hint to what the real type is, we need to figure it out ourselves
  if (stringMatch == null && numberMatch == null && booleanMatch == null) {
    numberMatch = ONLY_NUMBER_REGEX.exec(text);
    if (numberMatch == null) {
      booleanMatch = ONLY_BOOLEAN_REGEX.exec(text);
    }
  }
  if (numberMatch != null) {
    const rawValue = capMaxFloatDecimalDigits(parseFloat(text.replace(/\s/g, '')));

    // Calculate the default precision to apply to the number.
    // "Precision" refers to the number of decimal digits that should appear to the right of the decimal point.
    // "Default precision" refers to the precision applied to the formatted value if precision is not
    // explictly set on the cell in which the value is contained.
    let defaultDecimalPrecision;
    const rawDigitsBeforeDecimal = String(numberMatch[1] || '');
    const rawDigitsAfterDecimal = String(numberMatch[2] || numberMatch[3] || '.').replace('.', '');
    const allDigitsMinusLeadingZeros = stripLeadingZeros(
      rawDigitsBeforeDecimal.concat(rawDigitsAfterDecimal)
    );
    const defaultScientificPrecision = Math.max(
      Math.min(allDigitsMinusLeadingZeros.length, MAX_FLOAT_DECIMAL_DIGITS) - 1,
      0
    );
    defaultDecimalPrecision = rawDigitsAfterDecimal.length > 0 ? rawDigitsAfterDecimal.length : null;
    // Detect when the user has entered a value that should have its precision truncated because it exceeds
    // the maximum decimal precision we store. If detected, reduce the defaultPrecision appropriately
    if (allDigitsMinusLeadingZeros.length > MAX_FLOAT_DECIMAL_DIGITS) {
      // If there are non-zero digits to the left of the decimal point, we reduce the default precision
      // below MAX_FLOAT_DECIMAL_DIGITS proportionally. For example, 123456789.123456 has 9 digits before the
      // decimal point, so it can only have 6 digits after the decimal point, since
      // 6 + 9 = MAX_FLOAT_DECIMAL_DIGITS.  If we had another digit after the decimal point,
      // e.g. 123456789.1234567, we would have one excessive digit if we used a default precision of 7. Thus,
      // we reduce defaultPrecision to 6. The raw value will have already been rounded to 123456789.123457
      // above.
      // For values less than zero, we can extend defaultPrecision beyond MAX_FLOAT_DECIMAL_DIGITS if the
      // fraction contains leading zeros, but we still won't attempt to render more than
      // MAX_FLOAT_DECIMAL_DIGITS significant digits. For example, 0.0000000000000000123 has 19 digits after
      // the decimal point, but only 3 decimal digits, which is acceptable (as 3 <= MAX_FLOAT_DECIMAL_DIGITS).
      const integerDigitsMinusLeadingZeros = stripLeadingZeros(rawDigitsBeforeDecimal);
      if (integerDigitsMinusLeadingZeros.length > 0) {
        defaultDecimalPrecision = Math.max(
          MAX_FLOAT_DECIMAL_DIGITS - integerDigitsMinusLeadingZeros.length,
          0
        );
      } else {
        const significantDigitsAfterDecimal = stripLeadingZeros(rawDigitsAfterDecimal);
        const numLeadingZeros = rawDigitsAfterDecimal.length - significantDigitsAfterDecimal.length;
        defaultDecimalPrecision =
          Math.min(significantDigitsAfterDecimal.length, MAX_FLOAT_DECIMAL_DIGITS) + numLeadingZeros;
      }
    }

    if (numberMatch[4] != null) {
      return astValue({
        type: BaseTypedValueType.SCIENTIFIC,
        rawValue,
        defaultPrecision: defaultScientificPrecision,
      }) as ScientificTypedValue;
    } else {
      return astValue({
        type: BaseTypedValueType.NUMBER,
        rawValue,
        defaultPrecision: defaultDecimalPrecision,
      }) as NumberTypedValue;
    }
  } else if (booleanMatch != null) {
    return astValue({
      type: BaseTypedValueType.BOOLEAN,
      booleanValue: booleanMatch[1] != null,
    }) as BooleanTypedValue;
  } else {
    return astValue({type: BaseTypedValueType.STRING, rawValue: text}) as StringTypedValue;
  }
}

// Expects number to be zero-indexed.
export function colNumberToLetters(number: number): string {
  let letters = '';
  while (number >= 0) {
    letters = String.fromCharCode((number % NUM_LETTERS) + START_LETTER) + letters;
    number = Math.floor(number / NUM_LETTERS) - 1;
  }

  return letters;
}

export function colLettersToNumbers(letters: string): number {
  letters = letters.toUpperCase();
  let number = 0;
  for (const letter of Array.from(letters)) {
    number = number * 26 + (letter.charCodeAt(0) - START_LETTER + 1);
  }

  // Return the number zero-indexed
  return number - 1;
}

// Expects number to be zero-indexed
export function rowIndexToNumber(index: number): number {
  return index + 1;
}

export function toSpreadsheetIndex(cellId: RelativeCellAddress, locks: Partial<CellLock> = {}): string {
  const i = cellId.row;
  const rowLock = locks.row ? '$' : '';

  const j = cellId.column;
  const colLock = locks.col ? '$' : '';

  return `${colLock}${colNumberToLetters(j)}${rowLock}${i + 1}`;
}

export function escapeTableName(tableName: string): string {
  // tableName.indexOf('!') === -1 &&
  if (/^(?!!)\w+$/.test(tableName)) {
    return tableName;
  }
  const newTableName = tableName.replace(/'/g, "''");
  return `'${newTableName}'`;
}

/**
 * Generates a prefix for table references based on document and table names
 */
export function getDocTableReferencePrefix(document?: string | null, table?: string | null): string {
  if (document && table) {
    // Document-qualified reference: 'Document Name'!'Table Name'!
    const escapedDoc = escapeTableName(document);
    const escapedTable = escapeTableName(table);
    return `${escapedDoc}!${escapedTable}!`;
  } else if (table) {
    // Regular table reference: 'Table Name'!
    return `${escapeTableName(table)}!`;
  }
  return '';
}

/**
 * @param  {Object} cellId     Index of location of cell, represented as {row, column, table}
 *                                 {Number} row      Index of row containing cell
 *                                 {Number} column   Index of column containing cell
 *                                 {String} table    nullable name of table
 * @return {String} Text reference to a single cell in the table. The tablePrefix is omitted if tableName is
 *                  null.
 */
export function toCellReferenceTokenText(cellId: RelativeCellAddress, locks: Partial<CellLock> = {}): string {
  const prefix = getDocTableReferencePrefix(cellId.document, cellId.table);
  return `${prefix}${toSpreadsheetIndex(cellId, locks)}`;
}

/**
 * @param  {Object} rangeStartIndex     Index of location of start cell of range, represented as
 *                                      {row, column, table}
 *                                          {Number} row      Index of row containing cell
 *                                          {Number} column   Index of column containing cell
 *                                          {String} table    nullable name of table
 * @param  {Object} rangeEndIndex       Index of location of end cell of range, represented as
 *                                      {row, column, table}
 *                                          {Number} row      Index of row containing cell
 *                                          {Number} column   Index of column containing cell
 *                                          {String} table    nullable name of table
 * @return {String} Text reference to a range of cells in the table. The tablePrefix is omitted if tableName
 *                  is null.
 */
export function toCellRangeReferenceTokenText(
  rangeStartId: RelativeCellAddress,
  rangeEndId: RelativeCellAddress,
  locks: Partial<RangeLock> = {}
): string {
  assert(rangeStartId.table === rangeEndId.table, 'Table name for start and end of range do not match');
  assert(
    rangeStartId.document === rangeEndId.document,
    'Document name for start and end of range do not match'
  );

  const prefix = getDocTableReferencePrefix(rangeStartId.document, rangeStartId.table);

  return `${prefix}\
${toSpreadsheetIndex(rangeStartId, {row: locks.startRow, col: locks.startCol})}:\
${toSpreadsheetIndex(rangeEndId, {row: locks.endRow, col: locks.endCol})}`;
}

/**
 * Returns an object that is deep equal to `source`, while re-using as many subcomponents of
 * `base` as possible. `base` should be an object with structural symmetry to `source`.
 */
export function cloneWithReuse<T extends Object | null | undefined>({
  base,
  source,
}: {
  base: T | null | undefined;
  source: T;
}): T {
  if (source === base) {
    return base as T;
  }
  if (source == null || base == null) {
    return source;
  }
  if (source.constructor === Object && base.constructor === Object) {
    return _cloneHelper({
      base,
      source,
      // _.mapObject behaves expectedly
      // eslint-disable-next-line @typescript-eslint/unbound-method
      makeMap: _.mapObject,
      getSize(obj: {[key: string]: unknown}): number {
        return Object.keys(obj).length;
      },
    });
  }
  if (source.constructor === Array && base.constructor === Array) {
    return _cloneHelper<Array<unknown>>({
      base,
      source,
      // _.map behaves expectedly
      // eslint-disable-next-line @typescript-eslint/unbound-method
      makeMap: _.map,
      getSize(arr: Array<unknown>): number {
        return arr.length;
      },
    }) as unknown as T;
  }
  return source;
}

function _cloneHelper<T>({
  base,
  source,
  makeMap,
  getSize,
}: {
  base: T;
  source: T;
  makeMap: (t: T, mapFn: <U>(value: U, key: number | string) => U) => T;
  getSize: (object: T) => number;
}): T {
  let isSubset = true;
  const optimizedSource = makeMap(source, (sourceValue, key) => {
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const originalValue = (base as any)[key];
    const optimizedValue = cloneWithReuse({base: originalValue, source: sourceValue});
    isSubset = isSubset && optimizedValue === originalValue;
    return optimizedValue;
  });
  if (isSubset && getSize(optimizedSource) === getSize(base)) {
    return base;
  } else {
    return optimizedSource;
  }
}

export function uniqueTableName(
  tableNames: Array<string> = [],
  prefix: string = TABLE_NAME_DEFAULT_PREFIX,
  {allowNoSuffix = false}: {allowNoSuffix?: boolean} = {}
): string {
  return generateUniqueName(tableNames, prefix, {allowNoSuffix});
}

export function makeUniqueNameFactory(
  usedNames: Array<string>
): (prefix: string, options?: {allowNoSuffix?: boolean}) => string {
  const tempList: Array<string> = [...usedNames];
  return (prefix: string, {allowNoSuffix = false}: {allowNoSuffix?: boolean} = {}): string => {
    const nextName = uniqueTableName(tempList, prefix, {allowNoSuffix});
    tempList.push(nextName);
    return nextName;
  };
}

export function extractTableNamePrefix(tableName: string): string {
  const match = /^(.*?)\d+$/.exec(tableName);
  if (match != null) {
    return match[1];
  }
  return tableName;
}

export function getFormulaText(text: string): string | null {
  if (text.indexOf('=') !== 0) {
    return null;
  }
  return text.slice(1);
}

export function momentToDaysSinceEpoch(date: moment.Moment): number {
  // Convert moment object to days since excess epoch.
  return momentToDaysSince(date, EPOCH_ZERO_DATE);
}

export function momentToDaysSince(date: moment.Moment, reference: moment.Moment): number {
  return date.diff(reference, 'days', /* asFloat */ true);
}

export function daysSinceEpochToDatetimeString(daysSinceEpoch: number): string {
  // Convert a number of days since excess epoch to a parsable datetime string.
  // Must separately add days and seconds because moment does not support adding
  // non-integer numbers of days or months due to how calendars work.
  const numDays = Math.trunc(daysSinceEpoch);
  const fractionalDays = daysSinceEpoch % 1;
  const numSeconds = Math.round(fractionalDays * SECONDS_PER_DAY);
  const momentResult = moment(EPOCH_ZERO_DATE).add(numDays, 'days').add(numSeconds, 'seconds');
  if (momentResult.hour() || momentResult.minute() || momentResult.second()) {
    return momentResult.format(USER_EDIT_DATETIME_FORMAT);
  } else {
    return momentResult.format(USER_EDIT_DATE_FORMAT);
  }
}

export function hasLoadingCells(outputTableMap: OutputTableMap): boolean {
  for (const evaluatedTable of Object.values(outputTableMap)) {
    for (const evaluatedRow of evaluatedTable) {
      for (const evaluatedCell of evaluatedRow) {
        if (isCellLoading(evaluatedCell)) {
          return true;
        }
      }
    }
  }
  return false;
}

export function isCellLoading(evaluatedCell: EvaluatedCell): boolean {
  return hasErrorType(evaluatedCell.error, LoadingError.type);
}

function isBypassable(error: BaseCellError): boolean {
  return (
    BenchlingConfig.STRUCTURED_TABLE_ENABLE_VALIDATION_BYPASS &&
    error.type === ValidationRuleError.type &&
    (error as ValidationRuleError).overridable
  );
}

function isWarning(error: BaseCellError): boolean {
  return (
    error.type === Warning.type ||
    error.type === BypassedValidationRuleWarning.type ||
    error.type === Info.type
  );
}

export function hasAnyWarnings(error: PotentialCellErrors): boolean {
  return hasErrorType(error, Warning.type) || hasErrorType(error, BypassedValidationRuleWarning.type);
}

// This will return true if the only errors are bypassed, but there must be at least one bypassed error.
// If there are no errors or any other non-warning blocking errors, this will return false.
export function hasOnlyBypassedErrors(error: PotentialCellErrors): boolean {
  if (!error) {
    return false;
  }
  if (Array.isArray(error)) {
    if (error.length === 0) {
      return false;
    }
    return (
      error.every((error) => isWarning(error)) &&
      error.some((error) => hasErrorType(error, BypassedValidationRuleWarning.type))
    );
  }
  return hasErrorType(error, BypassedValidationRuleWarning.type);
}

export function hasOnlyBypassableErrors(error: PotentialCellErrors): boolean {
  if (!error) {
    return false;
  }
  if (Array.isArray(error)) {
    if (error.length === 0) {
      return false;
    }
    return error.every((error) => isBypassable(error));
  }
  return isBypassable(error);
}

// This returns true if there are any errors that are not just warnings
export function hasAnyErrors(error: PotentialCellErrors): boolean {
  if (!error) {
    return false;
  }
  if (Array.isArray(error)) {
    if (error.length === 0) {
      return false;
    }
    return error.some((error) => !isWarning(error));
  }
  return !isWarning(error);
}

export function hasAnyBlockingErrors(error: PotentialCellErrors): boolean {
  if (!error) {
    return false;
  }
  if (Array.isArray(error)) {
    if (error.length === 0) {
      return false;
    }
    return error.some((error) => !isNonBlockingError(error));
  }
  return !isNonBlockingError(error);
}

/*
  This means non-blocking in the context of evaluation.  So all validation errors count as non-blocking
  whether they are overridable or not.
*/
function isNonBlockingError(error: BaseCellError): boolean {
  return (
    error.type === Warning.type ||
    error.type === Info.type ||
    isBypassable(error) ||
    error.type === BypassedValidationRuleWarning.type ||
    error.type === ComputedEntityFieldError.type
  );
}

export function hasOnlyNonBlockingErrors(error: PotentialCellErrors): boolean {
  if (!error) {
    return true;
  }
  if (Array.isArray(error)) {
    return error.every((error) => isNonBlockingError(error));
  }
  return isNonBlockingError(error);
}

// This is used to determine if a cell is in an error state that should prevent evaluation
// This is all errors expept for: Warnings, Info, and any type of validation error
export function isInvalidCellError(error: BaseCellError): boolean {
  return !(isNonBlockingError(error) || error.type === ValidationRuleError.type);
}

export function hasInvalidCellError(error: PotentialCellErrors): boolean {
  if (!error) {
    return false;
  }
  if (Array.isArray(error)) {
    return error.some((error) => isInvalidCellError(error));
  }
  return isInvalidCellError(error);
}

export function hasErrorType(error: PotentialCellErrors, type: string): boolean {
  if (!error) {
    return false;
  }
  if (Array.isArray(error)) {
    return error.some((error) => error.type === type);
  }
  return error.type === type;
}

export function containsError(error: PotentialCellErrors, errorToCheck: BaseCellError): boolean {
  if (!error) {
    return false;
  }
  if (Array.isArray(error)) {
    return error.some((error) => areErrorsEqual(error, errorToCheck));
  }
  return areErrorsEqual(error, errorToCheck);
}

function areErrorsEqual(error1: BaseCellError, error2: BaseCellError): boolean {
  return error1.type === error2.type && error1.message === error2.message;
}

/**
 * Checks to see if the superset has the same errors as the subset.
 *
 * If neither has errors, this will return true.
 * If there is any mismatch between the two, then it will return false.
 */
export function areErrorsSubset(superset: PotentialCellErrors, subset: PotentialCellErrors): boolean {
  if (!superset) {
    return !subset;
  }
  if (!subset) {
    return false;
  }
  if (Array.isArray(superset)) {
    if (Array.isArray(subset)) {
      return subset.every((error) => containsError(superset, error));
    }
    return containsError(superset, subset);
  }
  if (Array.isArray(subset)) {
    return subset.length === 1 && areErrorsEqual(subset[0], superset);
  }
  return areErrorsEqual(superset, subset);
}

/**
 * This is used to sort errors to display them in priority order.  A higher number means a more severe error
 * which will be displayed first.
 * @param error
 * @returns priority of the error, higher priority means more severe error
 */
function getErrorTypePriority(error: BaseCellError): number {
  if (error.type === MergeError.type) {
    return 6;
  }
  if (!isNonBlockingError(error)) {
    return 5;
  }
  if (error.type === ValidationRuleError.type) {
    return 4;
  }
  if (error.type === BypassedValidationRuleWarning.type) {
    return 3;
  }
  if (error.type === Warning.type) {
    return 2;
  }
  if (error.type === Info.type) {
    return 1;
  }
  assertAndContinue(false, `Error type missing priority: ${error.type}`);
  return 0;
}

export function sortErrors(errors: BaseCellErrors): BaseCellErrors {
  if (!Array.isArray(errors)) {
    return errors;
  }
  return errors.sort((a, b) => getErrorTypePriority(b) - getErrorTypePriority(a));
}

function addErrorWithoutDuplicates(errors: BaseCellErrors, newError: BaseCellError): BaseCellErrors {
  if (Array.isArray(errors)) {
    if (errors.some((error) => error.type === newError.type && error.message === newError.message)) {
      return errors;
    }
    return [...errors, newError];
  } else {
    if (errors.type === newError.type && errors.message === newError.message) {
      return errors;
    }
    return [errors, newError];
  }
}

/**
 * This will combine two errors into a single error, handling any cases where either is an array or either
 * is null/undefined.
 * If both are null/undefined, it will return null.
 * This will inherently remove duplicates.
 */
export function addError(
  existingError: PotentialCellErrors,
  newError: PotentialCellErrors
): PotentialCellErrors {
  if (!newError) {
    return existingError ?? null;
  }
  if (!existingError) {
    return newError;
  }
  if (Array.isArray(newError)) {
    let errors: BaseCellErrors = existingError;
    for (const error of newError) {
      errors = addErrorWithoutDuplicates(errors, error);
    }
    return errors;
  } else {
    return addErrorWithoutDuplicates(existingError, newError);
  }
}

export function getErrorText(error: PotentialCellErrors): string {
  // TODO-tygary: Resolve the order here, how do we want to handle multiple errors?
  if (Array.isArray(error)) {
    return '#ERROR!';
  }
  if (error instanceof CellError) {
    return error.text;
  }
  return '#ERROR!';
}

/**
 * The error output we put on the evaludatedCell should only include the type and message, not the text or
 * anything else that has been decorated onto the CellError class.
 */
function toErrorOutput(error: BaseCellError): BaseCellError {
  return {type: error.type, message: error.message};
}

export function getErrorOutput(error: PotentialCellErrors): BaseCellErrors | undefined {
  if (Array.isArray(error)) {
    if (error.length === 0) {
      return undefined;
    }
    return error.map((error) => toErrorOutput(error));
  }
  if (error) {
    return toErrorOutput(error);
  }
  return undefined;
}

/**
 * Combines the errors into a single string, separated by commas.
 * This should only be used for logging purposes.
 */
export function getCombinedErrorString(errors: PotentialCellErrors): string {
  if (!errors) {
    return '';
  }
  if (Array.isArray(errors)) {
    return errors.map((error) => error.message).join(', ');
  }
  return errors.message;
}

/**
 * Apply precedence logic to determine if a merged child cell's error should bubble up to the parent cell:
 * 1. If any cell is loading, merge range should display a loading error
 * 2. If a merge error is detected, merged range should display that, unless something is still loading
 * 3. If the parent merge cell doesn't have an error, display any combined errors encountered in children
 *
 * @param childError the error in the child merge cell
 * @param currentBubbledError the current value of the error to bubble up
 * @returns the new or existing bubbled error value
 */
export function computeBubbledError(
  childError: PotentialCellErrors,
  currentBubbledError: PotentialCellErrors
): PotentialCellErrors {
  if (hasErrorType(currentBubbledError, LoadingError.type)) {
    return currentBubbledError;
  }
  if (hasErrorType(childError, LoadingError.type)) {
    return childError;
  }
  return addError(currentBubbledError, childError);
}

/**
 * Bubbles validation errors up to the parent merged cell.
 *
 * This goes only one way, up to the parent, and will not modify the child errors.
 * If any mismatch is detected between the errors in any of the merged cells, then the parent
 * cell will get a MergeError.
 *
 * This differentiated between a single input and a merged cell and uses different messages.
 *
 * @param table the table to bubble the errors up in.  This will be modified in place.
 */
export function bubbleMergedCellValidationErrors(table: Table): void {
  for (let i = 0; i < table.table.length; i++) {
    const row = table.table[i];
    for (let j = 0; j < row.length; j++) {
      const parent = row[j];
      const rowSpan = parent.mergeRowSpan ?? 1;
      const colSpan = parent.mergeColSpan ?? 1;
      if (rowSpan === 1 && colSpan === 1) {
        continue;
      }

      const isSingleInputColumn = table.columnConstraints[j]?.structuredTableColumn?.isSingleInput ?? false;
      // Sets the mergedBox property and the evaluated value.
      let bubbledErrors: PotentialCellErrors = parent.error ?? null;
      for (let s = i; s < i + rowSpan; s++) {
        for (let t = j; t < j + colSpan; t++) {
          const child = table.table[s][t];
          if (bubbledErrors && !areErrorsSubset(bubbledErrors, child.error)) {
            const mergeMessage = isSingleInputColumn
              ? 'Conflicting validation errors found. Convert to column to resolve errors individually.'
              : 'Merged cells contain conflicting validation errors. Unmerge to resolve errors individually.';
            bubbledErrors = addError(bubbledErrors, new MergeError(mergeMessage));
          }
          bubbledErrors = addError(bubbledErrors, child.error);
        }
      }
      parent.error = bubbledErrors;
    }
  }
}

function extractCellErrorFromTypedValue(typedValue: TypedValue): CellError | undefined {
  if (typedValue.type === BaseTypedValueType.ERROR) {
    return typedValue.error;
  }
  if (typedValue.type === BaseTypedValueType.ARRAY) {
    for (const value of typedValue.rawValue) {
      const error = extractCellErrorFromTypedValue(value);
      if (error) {
        return error;
      }
    }
  }
  return undefined;
}

export function throwOnErrorTypedValue(typedValue: TypedValue): void {
  if (!BenchlingConfig.ELN_ENABLE_XLOOKUP_FORMULA_ERROR_FIX) {
    return;
  }
  const error = extractCellErrorFromTypedValue(typedValue);
  if (error) {
    throw error;
  }
}

export interface CellErrorJSON {
  type: string;
  text: string;
  message: string;
}

export const REQUIRED_FIELD_ERROR_MESSAGE = 'Value is required.';

export class CellError extends ErrorSubclass implements BaseCellError {
  type: string;
  text: string;

  constructor(message: string) {
    super(message);
    this.type = 'Error';
    this.text = '#ERROR!';
  }

  toJSON(): CellErrorJSON {
    return {type: this.type, message: this.message, text: this.text};
  }

  static fromJSON(errorJSON: CellErrorJSON): CellError {
    const {message, ...attributes} = errorJSON;
    const error = new CellError(message);
    Object.assign(error, attributes);
    return error;
  }
}

export class FormulaTokenizeError extends CellError {
  tokens?: Array<Token>;
  leftover?: string;

  constructor(message: string, info?: {tokens?: Array<Token>; leftover?: string}) {
    if (info == null) {
      info = {};
    }
    super(message);
    ({tokens: this.tokens, leftover: this.leftover} = info);
    this.type = 'Formula error';
  }
}

export class FormulaParseError extends CellError {
  constructor(message: string) {
    super(message);
    this.type = 'Formula error';
  }
}

export class ReferenceError extends CellError {
  static TEXT = '#REF!';

  constructor(message: string) {
    super(message);
    this.type = 'Reference error';
    this.text = ReferenceError.TEXT;
  }
}

export class ValueError extends CellError {
  static type = 'Value error';
  constructor(message: string) {
    super(message);
    this.type = ValueError.type;
    this.text = '#VALUE!';
  }
}

export function isRequiredFieldError(error: BaseCellError): boolean {
  return error.type === ValueError.type && error.message === REQUIRED_FIELD_ERROR_MESSAGE;
}

export class DivZeroError extends CellError {
  constructor(message: string) {
    super(message);
    this.text = '#DIV/0!';
  }
}

export class NameError extends CellError {
  constructor(message: string) {
    super(message);
    this.type = 'Name error';
    this.text = '#NAME!';
  }
}

export class NotApplicableError extends CellError {
  constructor(message: string) {
    super(message);
    this.text = '#N/A';
  }
}

export class NumericalError extends CellError {
  constructor(message: string) {
    super(message);
    this.type = 'Numerical error';
    this.text = '#NUM!';
  }
}

/**
 * This error is used to represent when a computed field on a custom entity has a failed computation.
 *
 * This is a non-blocking error since computed fields are computed asynchronously after the entity is created
 * or updated.
 */
export class ComputedEntityFieldError extends CellError {
  static type = 'Computed field error';
  constructor(message: string) {
    super(message);
    this.type = ComputedEntityFieldError.type;
    this.text = '#VALUE!';
  }
}

export class Warning extends CellError {
  static type = 'Warning';
  constructor(message: string) {
    super(message);
    this.type = 'Warning';
    this.text = '#VALUE!';
  }
}

export class Info extends CellError {
  static type = 'Info';
  constructor(message: string) {
    super(message);
    this.type = 'Info';
    this.text = '#VALUE!';
  }
}

export class LoadingError extends CellError {
  static type = 'Loading';
  constructor(message: string) {
    super(message);
    this.type = LoadingError.type;
    this.text = '#VALUE!';
  }
}

export class MergeError extends CellError {
  static type = 'Invalid merge';
  constructor(message: string) {
    super(message);
    this.type = MergeError.type;
    this.text = '#REF!';
  }
}

export class ValidationRuleError extends CellError {
  static type = 'Validation error';
  overridable: boolean;

  constructor(message: string, overridable: boolean) {
    super(message);
    this.type = ValidationRuleError.type;
    this.overridable = overridable;
  }
}

export class BypassedValidationRuleWarning extends CellError {
  static type = 'Bypassed validation';

  constructor(message: string) {
    super(message);
    this.type = BypassedValidationRuleWarning.type;
  }
}
